<template>
  <div class="page-business-intelligence-tenders">
    <!-- Phones: the table draws it, under the tabs that filter what it counts. -->
    <div v-if="!isPhone" class="__results-section" ref="topAnchor">
      <span class="__count">
        <span class="number-monospace">{{ formatLocalizedNumber(totalCount) }}</span>
        {{ ' ' + $tc('page.business-intelligence.results', totalCount) }}
      </span>
      <BaseRefreshButton :pending="tendersBiddedPending" @refresh="refreshTenders" />
    </div>
    <div class="__content">
      <!-- Phones: too many to fit across the screen; the one shown, the others in a sheet. -->
      <BaseTabsSelect
        v-if="isPhone"
        class="__tabs-select"
        :value="activeTab"
        :tabs="phoneTabs"
        @input="onSelectedTabChange"
      />
      <v-tabs v-else v-model="activeTab" @change="onSelectedTabChange">
        <v-tab v-for="tab in tabs" :key="tab.key" :disabled="tab.disabled">
          <BaseIcon v-if="tab.icon" :name="tab.icon" />
          <span>{{ tab.name }}</span>
        </v-tab>
      </v-tabs>
      <TableState
        v-if="!hasResults"
        :state-loading="!hasResults && tendersBiddedPending"
        :state-no-data="!hasResults && !tendersBiddedPending"
        :state-text="
          tendersBiddedPending ? $t('page.business-intelligence.loading') : $t('page.business-intelligence.no-result')
        "
      />
      <v-overlay :value="tendersBiddedPending" absolute opacity="0.5" color="#ffffff" z-index="1"></v-overlay>
      <div v-if="hasResults">
        <TableNormal
          :fixed-header="true"
          :headers="headers"
          :items="data"
          :items-per-page="itemsPerPage"
          hide-default-footer
          item-key="id"
          class="bi-tenders-table"
          :mobile-breakpoint="1420"
          :has-more="isPhone && page < totalPages"
          :loading-more="tendersBiddedLoadingMore"
          :load-more-error="tendersBiddedLoadMoreError"
          :pending="tendersBiddedPending"
          @update:sort-by="setSortingField"
          @update:sort-desc="setSortingOrder"
          @click:row="onTenderClicked"
          @load-more="loadMore"
        >
          <template #phone-count>
            <span class="number-monospace">{{ formatLocalizedNumber(totalCount) }}</span>
            {{ ' ' + $tc('page.business-intelligence.results', totalCount) }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="tendersBiddedPending" @refresh="refreshTenders" />
          </template>
          <template v-slot:header.awarded_to>
            <span class="__header">{{ $t('page.business-intelligence.data-table-titles.winner') }}</span>
            <span v-if="tabs[activeTab].key !== 'interested-tenderer'" class="__header">
              {{ $t('page.business-intelligence.data-table-titles.bids') }}
            </span>
          </template>
          <template v-slot:item.title="{ item }">
            <div :id="'item-' + item.id" class="__tender-title">
              <div>{{ item.title }}</div>
              <div v-if="item.matchedKeywords || matchesPending" class="__tender-coverage">
                <template v-if="item.matchedKeywords">
                  <v-popover v-if="item.matchedKeywords.length > 0" trigger="hover" placement="top">
                    <BaseTag tone="info" preset="secondary" icon="type">
                      {{
                        $tc('page.tenders.covered-by-keywords', item.matchedKeywords.length, {
                          count: item.matchedKeywords.length,
                        })
                      }}
                    </BaseTag>
                    <template v-slot:popover>
                      <div class="tenders-data-table__matched-keywords">
                        <Keyword v-for="(keyword, index) in item.matchedKeywords" :key="index" isResult>
                          {{ keyword }}
                        </Keyword>
                      </div>
                    </template>
                  </v-popover>
                  <BaseTag v-else tone="warning" preset="secondary" icon="type">
                    {{ $t('page.tenders.not-covered-by-keywords') }}
                  </BaseTag>
                </template>
                <BaseTag v-else-if="matchesPending" skeleton />
              </div>
            </div>
          </template>
          <template v-slot:item.start_date="{ item }">
            <div>{{ formatStandardDate(toMoment(item.start_date)) }}</div>
          </template>
          <template v-slot:item.end_date="{ item }">
            <div>{{ formatStandardDate(toMoment(item.end_date)) }}</div>
          </template>
          <template v-slot:item.awarded_to="{ item }">
            <div
              v-if="item.lots && item.lots.length > 0"
              class="__multiple-lots-container"
              :class="{ compact: tabs[activeTab].key === 'interested-tenderer' }"
            >
              {{ $tc('page.business-intelligence.multiple-lots', item.lots.length) }}
            </div>
            <div v-else class="__badge-list">
              <BIAwardsList
                :awards="item.awarded_to"
                :currentSupplier="item.supplier_name"
                :limit="awardLimit"
                :hasWinner="item.awarded_to && item.awarded_to.length > 0"
                :source="item.source"
                @add-competitor="onAddCompetitorClicked"
                @edit-competitor="onEditCompetitorClicked"
                @remove-winner="onRemoveWinnerClicked"
                @action-succeeded="onRefresh"
              />
              <!-- A phone row names this cell by its column's plain title ("Gagnant"): the bids, under
                   the winner there, need their own. -->
              <span
                v-if="
                  isPhone && tabs[activeTab].key !== 'interested-tenderer' && (!item.lots || item.lots.length === 0)
                "
                class="__bids-label"
              >
                {{ $t('page.business-intelligence.data-table-titles.bids') }}
              </span>
              <BIBidsList
                v-if="tabs[activeTab].key !== 'interested-tenderer' && (!item.lots || item.lots.length === 0)"
                :tenderId="item.id"
                :bids="item.bids"
                :limit="bidLimit"
                :currentSupplier="item.supplier_name"
                :hasWinner="item.awarded_to && item.awarded_to.length > 0"
                :source="item.source"
                @add-competitor="onAddCompetitorClicked"
                @edit-competitor="onEditCompetitorClicked"
                @add-winner="onAddWinnerClicked"
                @action-succeeded="onRefresh"
              />
            </div>
          </template>
          <template v-slot:item.interested_tenderers="{ item }">
            <div class="__badge-list">
              <BIPotentialBiddersList
                :tenderId="item.id"
                :potentialBidders="item.interested_tenderers"
                :limit="bidLimit"
                :hasWinner="item.awarded_to && item.awarded_to.length > 0"
                :source="item.source"
                @add-competitor="onAddCompetitorClicked"
                @edit-competitor="onEditCompetitorClicked"
                @add-winner="onAddWinnerClicked"
                @action-succeeded="onRefresh"
              />
            </div>
          </template>
          <template v-slot:item.actions="{ item }" v-if="isManager">
            <div class="__actions">
              <BaseMoreActions
                v-if="isPhone"
                :title="item.title"
                :actions="rowActions(item)"
                @select="onRowAction($event, item)"
              />
              <v-menu v-else>
                <template v-slot:activator="{ on, attrs }">
                  <div @click.stop v-on="on" v-bind="attrs">
                    <BaseIcon name="more-vertical" class="__more" />
                  </div>
                </template>
                <div class="__actions-menu base-actions-menu">
                  <ul>
                    <li @click="openManageBidsDialog(item)">
                      <BaseIcon name="vote" />
                      <span>{{ $t('page.business-intelligence.actions.manage-bids') }}</span>
                    </li>
                    <li @click="openManageAwardsDialog(item)">
                      <BaseIcon name="award" />
                      <span>{{ $t('page.business-intelligence.actions.manage-awards') }}</span>
                    </li>
                    <!-- TODO: reenable when doing https://github.com/umaneo/aloha.app/issues/3065 -->
                    <!-- <li @click="openManagePotentialBiddersDialog(item)">
                      <BaseIcon name="help-circle" />
                      <span>{{ $t('page.business-intelligence.actions.manage-potential-bidders') }}</span>
                    </li> -->
                  </ul>
                </div>
              </v-menu>
            </div>
          </template>
        </TableNormal>
      </div>
      <!-- Phones load more at the bottom of the list instead. -->
      <div class="bi-tenders-table__pagination" v-if="totalPages > 1 && !isPhone">
        <TablePagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="isMobile ? 6 : 10"
          @input="onChangePage"
        />
      </div>
    </div>
    <AddCompetitorDialog ref="addCompetitorDialog" @action-succeeded="onRefresh" />
    <EditCompetitorDialog ref="editCompetitorDialog" @action-succeeded="onRefresh" />
    <ManageBidsDialog ref="manageBidsDialog" />
    <ManageAwardsDialog ref="manageAwardsDialog" />
    <!-- TODO: reenable when doing https://github.com/umaneo/aloha.app/issues/3065 -->
    <!-- <ManagePotentialBidders ref="managePotentialBiddersDialog"  @on-refresh="onRefresh" /> -->
  </div>
</template>
<script>
import { bus } from '@/main';

import { mapActions, mapGetters } from 'vuex';
import { formatStandardDate, toMoment } from '@/util/date-util';
import { formatNumToCurrency } from '@/util/currency-util';
import { formatLocalizedNumber } from '@/util/number-util';
import { getSortingOrder } from '@/util/sorting-util';
import IsMobileMixin from '@/mixins/is-mobile-mixin';

import TableNormal from '@/components/table/TableNormal.vue';
import TableState from '@/components/table/TableState.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseTabsSelect from '@/components/base/BaseTabsSelect.vue';
import Keyword from '@/components/page-keywords/Keyword';
import BIBidsList from './BIBidsList.vue';
import BIAwardsList from './BIAwardsList.vue';
import BIPotentialBiddersList from './BIPotentialBiddersList.vue';
import AddCompetitorDialog from '@/views/business-intelligence/AddCompetitorDialog.vue';
import EditCompetitorDialog from '@/views/business-intelligence/EditCompetitorDialog.vue';
import ManageBidsDialog from '@/views/business-intelligence/ManageBidsDialog.vue';
import ManageAwardsDialog from '@/views/business-intelligence/ManageAwardsDialog.vue';
import { highlightPhoneRow } from '@/util/highlight-phone-row';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';

export default {
  name: 'PageBITenders',
  mixins: [IsMobileMixin],
  components: {
    BaseMoreActions,
    BaseRefreshButton,
    BaseTabsSelect,
    TableNormal,
    TableState,
    TablePagination,
    BIBidsList,
    BIAwardsList,
    BaseIcon,
    BaseTag,
    Keyword,
    BIPotentialBiddersList,
    AddCompetitorDialog,
    EditCompetitorDialog,
    ManageBidsDialog,
    ManageAwardsDialog,
  },
  props: {
    dates: {
      type: Array,
      required: true,
    },
    competitorId: {
      type: String,
      required: false,
      default: null,
    },
    // Lists the tenders published by this emitter instead of the tenant's bids.
    emitterId: {
      type: String,
      required: false,
      default: null,
    },
    hasTenderChanged: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapGetters('biDashboard', [
      'tendersBidded',
      'tendersBiddedPending',
      'matchesPending',
      'page',
      'totalPages',
      'totalCount',
      'tendersBiddedLoadingMore',
      'tendersBiddedLoadMoreError',
    ]),
    ...mapGetters('auth', ['isManager']),
    isPhone() {
      return viewport.size === 'phone';
    },
    headers() {
      return [
        {
          text: this.$t('page.business-intelligence.data-table-titles.title'),
          value: 'title',
        },
        {
          text: this.$t('page.business-intelligence.data-table-titles.client'),
          value: 'procuring_entity_name',
          phone: 'subtitle',
        },
        {
          text: this.$t('page.business-intelligence.data-table-titles.start-date'),
          value: 'start_date',
          width: '85px',
        },
        {
          text: this.$t('page.business-intelligence.data-table-titles.end-date'),
          value: 'end_date',
          width: '85px',
          align: 'left',
        },
        {
          text: this.$t('page.business-intelligence.data-table-titles.winner'),
          value: 'awarded_to',
          phone: 'block',
          sortable: false,
        },
        // show different columns based on potential bidder tab
        ...(this.tabs[this.activeTab].key === 'interested-tenderer'
          ? [
              {
                text: this.$t('page.business-intelligence.data-table-titles.interested-tenderer'),
                value: 'interested_tenderers',
                phone: 'block',
                sortable: false,
              },
            ]
          : []),
        {
          text: this.$t('page.business-intelligence.data-table-titles.actions'),
          sortable: false,
          align: 'center',
          value: 'actions',
          width: '100px',
        },
      ];
    },
    data() {
      if (!this.tendersBidded || this.tendersBidded.length === 0) {
        return [];
      }
      return this.tendersBidded;
    },
    hasResults() {
      return this.tendersBidded.length > 0;
    },
    tabs() {
      const tabs = [
        { key: 'all', name: this.$t('page.business-intelligence.tabs.all') },
        { key: 'unawarded', name: this.$t('page.business-intelligence.tabs.unawarded') },
        { key: 'won', name: this.$t('page.business-intelligence.tabs.won') },
        { key: 'lost', name: this.$t('page.business-intelligence.tabs.lost') },
        {
          key: 'interested-tenderer',
          name: this.$t('page.business-intelligence.tabs.interested-tenderer'),
        },
      ];
      if (!this.competitorId) {
        return tabs;
      }

      return [
        ...tabs,
        { key: 'in', name: this.$t('page.business-intelligence.tabs.in') },
        { key: 'out', name: this.$t('page.business-intelligence.tabs.out') },
      ];
    },
    // Keyed by position, as `activeTab` is.
    phoneTabs() {
      return this.tabs.map((tab, index) => ({ key: index, label: tab.name, disabled: tab.disabled }));
    },
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        { key: 'bids', label: this.$t('page.business-intelligence.actions.manage-bids'), icon: 'vote' },
        { key: 'awards', label: this.$t('page.business-intelligence.actions.manage-awards'), icon: 'award' },
      ];
    },
    onRowAction(key, item) {
      if (key === 'bids') this.openManageBidsDialog(item);
      else this.openManageAwardsDialog(item);
    },
    ...mapActions('biDashboard', [
      'getTendersBidded',
      'loadMoreTendersBidded',
      'reloadListedTendersBidded',
      'setCustomSortingField',
      'setCustomSortingOrder',
      'getAllCompetitors',
    ]),
    ...mapActions('biTenant', ['createAward', 'deleteAward']),
    toMoment,
    formatStandardDate,
    formatNumToCurrency,
    formatLocalizedNumber,
    query(page) {
      return {
        startDate: this.dates[0],
        endDate: this.dates[1],
        status: this.tabs[this.activeTab].key === 'all' ? null : this.tabs[this.activeTab].key,
        page: page,
        competitorId: this.competitorId,
        emitterId: this.emitterId,
      };
    },
    fetchTenders(page) {
      this.getTendersBidded(this.query(page));
    },
    // Phones list the tenders page after page.
    loadMore() {
      // Asking for a page now would cancel the search that's running.
      if (this.tendersBiddedPending || this.page >= this.totalPages) return;
      this.loadMoreTendersBidded(this.query(this.page + 1));
    },
    // The page on screen again; on a phone, every page listed, so the reader keeps their place.
    refreshTenders() {
      if (this.isPhone && this.page > 1) this.reloadListedTendersBidded(this.query());
      else this.fetchTenders(this.currentPage);
    },
    onChangePage(page = 1) {
      this.currentPage = page;
      this.fetchTenders(page);
    },
    onSelectedTabChange(tabIdx) {
      this.activeTab = tabIdx;
      this.fetchTenders(1);
    },
    getSortingField(field) {
      switch (field) {
        case 'title':
          return 'title';
        case 'procuring_entity_name':
          return 'client';
        case 'start_date':
          return 'start_date';
        case 'end_date':
          return 'end_date';
        default:
          return 'end_date';
      }
    },
    setSortingOrder(order) {
      const sortingOrder = getSortingOrder(order[0]);
      this.setCustomSortingOrder({ sortingOrder });
      this.fetchTenders(this.currentPage);
    },
    setSortingField(field) {
      const sortingField = this.getSortingField(field[0]);
      this.setCustomSortingField({ sortingField });
      this.fetchTenders(this.currentPage);
    },
    onAddCompetitorClicked(name) {
      this.$refs.addCompetitorDialog.open(name);
    },
    onEditCompetitorClicked(competitor, name) {
      this.$refs.editCompetitorDialog.open(competitor, name);
    },
    onAddWinnerClicked(tenderId, supplierName, amount) {
      this.createAward({
        tenderId,
        supplierName: supplierName,
        amount: amount || 0,
      })
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.create-success'));
          this.refreshTenders();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onRemoveWinnerClicked(id) {
      this.deleteAward(id)
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.delete-success'));
          this.refreshTenders();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onRefresh() {
      this.refreshTenders();
      // refresh competitors list
      this.getAllCompetitors();
    },
    onTenderClicked(tender) {
      this.$emit('tender-clicked', tender);
    },
    highlightTender(tenderId) {
      const elementId = `item-${tenderId}`;
      this.$nextTick(() => this.goToElement(elementId));
    },
    goToElement(elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        const tr = element.closest('tr');
        // Phones list the tenders, without table rows.
        if (!tr) {
          highlightPhoneRow(element);
          return;
        }
        tr.className = 'highlight';
        setTimeout(() => {
          tr.className = '';
        }, 1200);
      }
    },
    openManageBidsDialog(item) {
      const officialBidders = item.bids ? item.bids.filter((bid) => !bid.is_manual_entry) : [];
      this.$refs.manageBidsDialog.open(item.id, officialBidders.length > 0);
    },
    openManageAwardsDialog(item) {
      const officialWinners = item.awards ? item.awards.filter((award) => !award.is_manual_entry) : [];
      this.$refs.manageAwardsDialog.open(item.id, officialWinners.length > 0);
    },
    openManagePotentialBiddersDialog(item) {
      const officialPotentialBidders = item.interested_tenderers
        ? item.interested_tenderers.filter((award) => !award.is_manual_entry)
        : [];
      this.$refs.managePotentialBiddersDialog.open(item.id, officialPotentialBidders.length > 0);
    },
  },
  watch: {
    dates() {
      this.fetchTenders(1);
      this.currentPage = 1;
    },
    // A phone lists from the first page, and the larger screens show one page at a time.
    isPhone() {
      this.onChangePage(1);
    },
    hasTenderChanged() {
      this.onRefresh();
    },
  },
  data() {
    return {
      itemsPerPage: 25,
      currentPage: 1,
      activeTab: 0,
      bidLimit: 3,
      awardLimit: 1,
      lastViewedTenderId: null,
    };
  },
  async created() {
    this.setCustomSortingField({ sortingField: 'end_date' });
    this.setCustomSortingOrder({ sortingOrder: 'desc' });
    this.fetchTenders(1);
    this.getAllCompetitors();

    bus.$on('last-viewed-tender-id', (id) => {
      this.lastViewedTenderId = id;
      if (this.lastViewedTenderId) {
        this.$nextTick(() => {
          this.highlightTender(this.lastViewedTenderId);
          this.lastViewedTenderId = null;
        });
      }
    });
  },
  async mounted() {
    bus.$on('refresh-bi-dashboard', () => {
      this.refreshTenders();
    });
  },
  async beforeDestroy() {
    bus.$off('refresh-bi-dashboard');
  },
};
</script>

<style lang="scss">
.page-business-intelligence-tenders {
  > .__results-section {
    display: flex;
    align-items: center;
    padding-bottom: 8px;
    justify-content: space-between;

    .__count {
      color: $color-neutral;
      font-size: $font-size-small;
      padding: 4px 12px;
      text-transform: uppercase;
    }
  }

  > .__content {
    flex: auto;
    margin-bottom: 32px;
    position: relative;

    .__badge-list {
      display: flex;
      > span {
        margin-left: 8px;
      }

      // Phones: the winner, then the bids, each on its line rather than past the screen's edge.
      @include screen-below('tablet') {
        flex-wrap: wrap;
        gap: 8px;

        > span {
          margin-left: 0;
        }

        // As the row's other labels.
        .__bids-label {
          flex-basis: 100%;
          margin-top: 4px;
          font-size: 13px;
          color: $color-slate-500;
        }

        .business-intelligence-awards-list,
        .business-intelligence-bids-list,
        .business-intelligence-potential-bidders-list {
          flex-wrap: wrap;
          min-width: 0;
        }
      }
    }

    .__tender-title {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .__tender-coverage {
      display: flex;
      align-items: center;
    }

    .__multiple-lots-container {
      display: flex;
      width: 100%;
      min-height: 60px;
      max-width: 770px;
      border: $grey-border;
      border-radius: $border-radius-medium;
      align-items: center;
      padding: 8px;
      font-style: italic;
      font-size: $font-size-small;

      &.compact {
        max-width: 180px;
      }
    }

    > .__tabs-select {
      margin-bottom: $space-md;
    }

    > .v-tabs {
      border-top-left-radius: $border-radius-root;
      border-top-right-radius: $border-radius-root;

      .v-tab {
        padding: 0 12px;
        text-transform: none;
        color: $color-normal-pale;

        &.v-tab--active {
          color: $color-normal;
          font-weight: 600;
        }

        .base-icon {
          margin-right: 8px;
          height: 16px;
          width: 16px;
        }
      }
    }
  }

  .bi-tenders-table {
    &.v-data-table.theme--light {
      thead th {
        background-color: $color-white;
        border-bottom: 0.5px solid $color-border !important;

        .__header {
          min-width: 215px;
        }
      }

      .v-data-table-header {
        position: sticky;
        top: 128px;
        z-index: 10;
        border-bottom: 1px solid $color-border;
      }
    }
    tr th {
      font-size: $font-size-small !important;
    }
    tbody {
      td {
        padding: 8px !important; // reduce padding for BI table only, to be removed when we work on redesigning the tables
      }
    }

    &__pagination {
      position: fixed;
      bottom: 32px;
      left: 50%;
      transform: translateX(-50%);
    }
  }
}

// Same as TendersDataTable — popover content is teleported outside the BI page DOM.
.tenders-data-table__matched-keywords {
  max-width: min-content;
  flex-wrap: wrap;
  padding: 8px;
  gap: 4px;
  display: flex;
  flex-direction: column;
  justify-items: center;
}
</style>
